// ─── The reader's locale — ONE implementation, both entries ──────────
//
// Imported by the authenticated app (lib/format.ts and ~40 components) AND by
// the public status page (status/lib/format.ts). Deliberately its own module so
// there is exactly one copy: a second implementation of a behavioural rule is
// how the status API's field names drifted five times, and locale resolution is
// the same kind of quiet, easy-to-get-subtly-wrong logic.
//
// It cannot live in @pcp/shared: that package compiles with lib:ES2022 and no
// DOM lib, so `navigator` is TS2304 there — and the isolated Docker build would
// catch it where a local tsc (with ambient @types/node) does not.
//
// ── Why not just pass `undefined` ──
// Passing `undefined` is the obvious reading of "use the browser's settings",
// and it is WRONG. `undefined` means "the host's DEFAULT locale", which Chrome
// derives from the browser's UI language — not from the language preferences
// the user actually stated. Someone running an English Chrome with Danish as
// their content language reports:
//
//     navigator.languages      ["da", "en-US", "en"]     ← what they asked for
//     Intl…resolvedOptions()   "en-US"                   ← what `undefined` picks
//
// which is how the status page came to show "Aug 6, 2026, 5:21 PM" and "99.98%"
// to Danish customers (b-01559). CLAUDE.md's "pass undefined" is shorthand for
// "never hardcode a locale"; this is how that intent is actually delivered.

/** Intl throws a RangeError on a malformed tag, and no screen should fail to
 *  render over a browser setting — so tags are checked before they are used. */
const LANGUAGE_TAG_RE = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

let cached: readonly string[] | undefined;
let resolved = false;

/**
 * The user's stated language preferences, best-first, ready to hand to any
 * `toLocale*` / `Intl.*` call.
 *
 * Returns `undefined` — the host default, i.e. the old behaviour — only when
 * there is nothing usable to go on (no navigator, or nothing that parses as a
 * language tag). Memoised: these run per row per render, and the answer cannot
 * change without a reload.
 */
export function readerLocale(): readonly string[] | undefined {
  if (resolved) return cached;
  resolved = true;
  try {
    const nav = typeof navigator === 'undefined' ? null : navigator;
    const stated = nav?.languages?.length
      ? [...nav.languages]
      : nav?.language
        ? [nav.language]
        : [];
    const valid = stated.filter(
      (tag): tag is string => typeof tag === 'string' && LANGUAGE_TAG_RE.test(tag),
    );
    if (!valid.length) return (cached = undefined);
    // Prove the runtime accepts the whole list before handing it to every
    // formatter in the app.
    new Intl.DateTimeFormat(valid as string[]);
    cached = valid;
  } catch {
    cached = undefined;
  }
  return cached;
}
